import i18next from "i18next";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { initReactI18next } from "react-i18next";
import dyslexia from "../assets/Frame 2996-9/df.png";
import { fetchTranslations } from "../services/localizationService";
import { Widget, WidgetSettings } from "../types/widget";
import { postFeatureSelectMessage } from "../utils/message.helper";
import { Feature, Features } from "../constants/features";
import { publicAssetUrl } from "../config";

interface SettingsContextProps {
  oversizeWidget: boolean;
  setOversizeWidget: (value: boolean) => void;
  widgetButtonTextColor: string;
  widgetColor: string;
  setWidgetColor: (value: string) => void;
  widgetTextColor: string;
  setWidgetTextColor: (value: string) => void;
  selectedCheckBox?: any;
  setSelectedCheckBox?: any;
  setIsRight?: any;
  isRight?: any;
  buttonColor?: string;
  setButtonColor?: (value: string) => void;
  buttonTextColor?: string;
  setButtonTextColor?: (value: string) => void;
  widgetButtonColor?: string;
  setWidgetButtonColor?: (value: string) => void;
  setWidgetButtonTextColor?: (value: string) => void;
  toggleBgColor?: string;
  setToggleBgColor?: (value: string) => void;
  toggleTextColor?: string;
  setToggleTextColor?: (value: string) => void;
  toggleSwitchBgColor?: string;
  setToggleSwitchBgColor?: (value: string) => void;
  toggleSwitchTextColor?: string;
  setToggleSwitchTextColor?: (value: string) => void;
  widgetDataSettings?: Widget;
  selectedLanguage?: any;
  setSelectedLanguage?: any;
  horizontalDistance?: string;
  setHorizontalDistance?: (value: string) => void;
  loading?: boolean;
}

const SettingsContext = createContext<SettingsContextProps | undefined>(
  undefined
);

i18next.use(initReactI18next).init({
  fallbackLng: "english",
  interpolation: {
    escapeValue: false,
  },
});

export const SettingsProvider: React.FC<{
  children: React.ReactNode;
  lang: string;
}> = ({ children, lang }) => {
  const [oversizeWidget, setOversizeWidget] = useState<boolean>(() => {
    const saved = localStorage.getItem("oversizeWidget");
    return saved ? JSON.parse(saved) : false;
  });
  const [selectedCheckBox, setSelectedCheckBox] = useState<{
    selected: string;
    unSelected: string;
  }>(() => {
    const savedSelection = localStorage.getItem("selectedCheckBox");
    return savedSelection
      ? JSON.parse(savedSelection)
      : {
          selected: "",
          unSelected: "",
        };
  });
  const [isRight, setIsRight] = useState(true);
  const [buttonColor, setButtonColor] = useState<string>("#0048FF");
  const [buttonTextColor, setButtonTextColor] = useState<string>("#FFFFFF");
  const [widgetButtonColor, setWidgetButtonColor] = useState<string>("#0048FF");
  const [widgetButtonTextColor, setWidgetButtonTextColor] =
    useState<string>("#FFFFFF");
  const [widgetColor, setWidgetColor] = useState<string>("#0048FF");
  const [widgetTextColor, setWidgetTextColor] = useState<string>("#FFFFFF");
  const [translations, setTranslations] = useState<any[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<any>(null);
  const [widgetDataSettings, setWidgetDataSettings] = useState<Widget | null>(null);
  const [horizontalDistance, setHorizontalDistance] = useState<string>("20px");
  const processedLoadWidget = useRef(false);
  const [loading, setLoading] = useState(true);

  const slidesData: {
    [key: string]: { text: string; image: string; label: string }[];
  } = {
    [Features.CONTENT_BIGGER_TEXT]: [
      { text: Features.DEFAULT, image: publicAssetUrl("text.png"), label: "default" },
      {
        text: Features.CONTENT_BIGGER_TEXT_3XL,
        image: publicAssetUrl("text.png"),
        label: "3_xl_bigger_text",
      },
      {
        text: Features.CONTENT_BIGGER_TEXT_2XL,
        image: publicAssetUrl("text.png"),
        label: "2_xl_bigger_text",
      },
      { text: Features.CONTENT_BIGGER_TEXT_XL, image: publicAssetUrl("text.png"), label: "xl_bigger_text" },
      { text: Features.CONTENT_BIGGER_TEXT, image: publicAssetUrl("text.png"), label: "bigger_text" },
    ],
    [Features.CONTENT_TEXT_SPACING]: [
      { text: Features.DEFAULT, image: publicAssetUrl("textspace.png"), label: "default" },
      { text: Features.CONTENT_TEXT_SPACING_HEAVY, image: publicAssetUrl("textspace.png"), label: "heavy_space" },
      {
        text: Features.CONTENT_TEXT_SPACING_MODERATE,
        image: publicAssetUrl("textspace.png"),
        label: "moderate_space",
      },
      { text: Features.CONTENT_TEXT_SPACING_LIGHT, image: publicAssetUrl("textspace.png"), label: "light_space" },
    ],
    [Features.CONTENT_DYSLEXIC_FRIENDLY]: [
      { text: Features.DEFAULT, image: publicAssetUrl("textalign.png"), label: "default" },
      { text: Features.CONTENT_LEGIBLE_FONTS, image: dyslexia, label: "legible_fonts" },
      {
        text: Features.CONTENT_DYSLEXIC_FRIENDLY,
        image: dyslexia,
        label: "dyslexic_friendly",
      },
      { text: Features.CONTENT_DYSLEXIC_FONT, image: dyslexia, label: "dyslexic_font" },
    ],
    [Features.CONTENT_LINE_HEIGHT]: [
      { text: Features.DEFAULT, image: publicAssetUrl("lineheight.png"), label: "default" },
      {
        text: Features.CONTENT_LINE_HEIGHT_2X,
        image: publicAssetUrl("lineheight.png"),
        label: "line_height_(2x)",
      },
      {
        text: Features.CONTENT_LINE_HEIGHT_1_75X,
        image: publicAssetUrl("lineheight.png"),
        label: "line_height_(1.75x)",
      },
      {
        text: Features.CONTENT_LINE_HEIGHT_1_5X,
        image: publicAssetUrl("lineheight.png"),
        label: "line_height(1.5x)",
      },
    ],
    [Features.CONTENT_TEXT_ALIGN]: [  
      { text: Features.CONTENT_ALIGN_LEFT, image: publicAssetUrl("textalign.png"), label: "align_left" },
      { text: Features.CONTENT_ALIGN_RIGHT, image: publicAssetUrl("textalign.png"), label: "align_right" },
      { text: Features.CONTENT_ALIGN_CENTER, image: publicAssetUrl("textalign.png"), label: "align_center" },
      {
        text: Features.CONTENT_ALIGN_JUSTIFY,
        image: publicAssetUrl("textalign.png"),
        label: "align_justify",
      },
    ],
    [Features.COLOR_SATURATION]: [
      { text: Features.DEFAULT, image: publicAssetUrl("blur.png"), label: "default" },
      { text: Features.COLOR_HIGH_SATURATION, image: publicAssetUrl("blur.png"), label: "high_saturation" },
      { text: Features.COLOR_LOW_SATURATION, image: publicAssetUrl("blur.png"), label: "low_saturation" },
      { text: Features.COLOR_DESATURATE, image: publicAssetUrl("blur.png"), label: "desaturate" },
    ],
    [Features.COLOR_CONTRAST_MODE]: [
      { text: Features.DEFAULT, image: publicAssetUrl("contrast.png"), label: "default" },
      {
        text: Features.COLOR_LIGHT_CONTRAST,
        image: publicAssetUrl("contrast.png"),
        label: "light_contrast",
      },
      { text: Features.COLOR_DARK_CONTRAST, image: publicAssetUrl("contrast.png"), label: "dark_contrast" },
    ],
  };

  const loadWidgetPromiseRef = useRef(Promise.resolve());
  useEffect(() => {
    let isProcessing = false;

    const applyStoredSettings = async () => {
      // Apply selected items
      const selectedItems = localStorage.getItem("selectedItems");

      if (selectedItems) {
        const parsedItems = JSON.parse(selectedItems);
        for (const item of parsedItems) {
          if (slidesData[item]) {
            const currentSlide = localStorage.getItem("currentSlide");
            if (currentSlide) {
              const parsedSlides = JSON.parse(currentSlide);
              const slideIndex = parsedSlides[item] || 0;
              if (slidesData[item][slideIndex]?.text !== "Default") {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Small delay between messages
                postFeatureSelectMessage(slidesData[item][slideIndex].text as Feature);
              }
            }
          } else {
            await new Promise((resolve) => setTimeout(resolve, 50));
            JSON.parse(selectedItems).forEach((item: Feature) => {
              postFeatureSelectMessage(item);
            });
          }
        }
      }

      // Apply accessibility profiles
      const selectedAccessibility = localStorage.getItem(
        "selectedAccessibility"
      );
      if (selectedAccessibility) {
        const profiles: Partial<Record<Feature, Feature[]>> = {
          [Features.PROFILE_BLIND]: [Features.NAVIGATION_SCREEN_READER],
          [Features.PROFILE_LOW_VISION]: [
            Features.NAVIGATION_BIG_CURSOR,
            Features.COLOR_HIGH_SATURATION,
            Features.CONTENT_BIGGER_TEXT,
          ],
          [Features.PROFILE_COLOR_BLIND]: [Features.COLOR_SMART_CONTRAST],
          [Features.PROFILE_SEIZURE_EPILEPTIC]: [
            Features.COLOR_LOW_SATURATION,
            Features.NAVIGATION_PAUSE_ANIMATION,
          ],
          [Features.PROFILE_ADHD]: [
            Features.CONTENT_DYSLEXIC_FRIENDLY,
            Features.NAVIGATION_READING_MASK,
            Features.NAVIGATION_PAUSE_ANIMATION,
          ],
          [Features.PROFILE_MOTOR_IMPAIRED]: [Features.NAVIGATION_PAUSE_ANIMATION],
          [Features.PROFILE_DYSLEXIA]: [Features.CONTENT_DYSLEXIC_FRIENDLY],
          [Features.PROFILE_COGNITIVE_LEARNING]: [
            Features.NAVIGATION_READING_GUIDE,
            Features.CONTENT_BIGGER_TEXT,
            Features.NAVIGATION_PAUSE_ANIMATION,
          ],
        };

        const parsedSelectedAccessibility = JSON.parse(
          selectedAccessibility
        ) as Feature;
        if (
          parsedSelectedAccessibility &&
          profiles[parsedSelectedAccessibility]
        ) {
          for (const setting of profiles[parsedSelectedAccessibility]!) {
            await new Promise((resolve) => setTimeout(resolve, 50)); // Small delay between messages
            postFeatureSelectMessage(setting);
          }
        }
      }
    };

    const messageHandler = async (event: MessageEvent) => {
      if (isProcessing) return; // Prevent concurrent processing

      if (event.data.action === "load-widget") {
        if (processedLoadWidget.current) {
          return;
        }
        processedLoadWidget.current = true;
        isProcessing = true;
        try {
          const widgetData1 = event.data.data;
          setWidgetDataSettings(widgetData1?.widget as Widget);

          // Parse the widget settings
          const widgetSettings = widgetData1?.widget?.settings as WidgetSettings;

          if (!widgetSettings) {
            console.error("No widget settings found");
            return;
          }

          // Handle translations
          const language = widgetSettings.advanced?.defaultLanguage;
          if (language) {
            const fetchedTranslations = await fetchTranslations(language);
            i18next.addResourceBundle(
              lang,
              "translation",
              fetchedTranslations,
              true,
              true
            );
            await i18next.changeLanguage(lang);
          }

          // Handle widget position
          const desktopAnchor = widgetSettings.position?.positionOpen?.desktop;

          if (desktopAnchor) {
            const { anchor: horizontalAnchor, distance } = desktopAnchor;
            setHorizontalDistance(`${distance}px`);
            setIsRight(horizontalAnchor === "right");
          }

          // Allow styles to be applied
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Apply stored settings
          await applyStoredSettings();
        } catch (error) {
          console.error("Failed to process widget data:", error);
        } finally {
          isProcessing = false;
          setLoading(false);
        }
      }
    };

    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("oversizeWidget", JSON.stringify(oversizeWidget));
  }, [oversizeWidget]);

  useEffect(() => {
    const handleShortCutKeys = (event: MessageEvent) => {
      if (event.data.action === "shortcutKey" && event.data.altTrue) {
        switch (event.data.data.toLowerCase()) {
          case "r":
            break;
          default:
            break;
        }
      }
    };
    window.addEventListener("message", handleShortCutKeys);
    return () => window.removeEventListener("message", handleShortCutKeys);
  }, []);

  const [toggleSwitchBgColor, setToggleSwitchBgColor] =
    useState<string>("#ddd");
  const [toggleSwitchTextColor, setToggleSwitchTextColor] =
    useState<string>("#3b82f6");
  return (
    <SettingsContext.Provider
      value={{
        setIsRight,
        isRight,
        oversizeWidget,
        setOversizeWidget,
        selectedCheckBox,
        setSelectedCheckBox,
        buttonColor,
        setButtonColor,
        buttonTextColor,
        setButtonTextColor,
        widgetButtonColor,
        setWidgetButtonColor,
        widgetButtonTextColor,
        setWidgetButtonTextColor,
        widgetColor,
        setWidgetColor,
        widgetTextColor,
        setWidgetTextColor,
        widgetDataSettings,
        selectedLanguage,
        setSelectedLanguage,
        horizontalDistance,
        setHorizontalDistance,
        toggleSwitchBgColor,
        setToggleSwitchBgColor,
        toggleSwitchTextColor,
        setToggleSwitchTextColor,
        loading,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextProps => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
